import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  applyIfLatest,
  latestRequestInitialState,
  trackLatestRequests,
} from 'state/latestRequest';
import {
  getActiveUsers,
  getBlockedUsers,
  getFinanceStatistics,
  getNewcomers,
} from 'state/thunks/statisticsThunk/statisticsThunk';

const initialState = {
  newcomers: {},
  active: {},
  blocked: {},
  finances: [],
  ...latestRequestInitialState,
};

/**
 * Dashboard statistics.
 *
 * Every case here is guarded by `applyIfLatest`. These four reads are all
 * filtered by a month and a year the admin can change, and each section has
 * its own dropdown — so overlapping requests are normal operation, not an edge
 * case. Writing whichever response happened to arrive last is what made the
 * same dashboard show different numbers on consecutive loads.
 */
const slice = createSlice({
  name: 'statistics',
  initialState,
  reducers: {
    setLogOut: () => initialState,
  },
  extraReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder.addCase(
      getNewcomers.fulfilled,
      applyIfLatest(getNewcomers, (state, payload) => {
        state.newcomers = payload;
      }),
    );
    builder.addCase(
      getActiveUsers.fulfilled,
      applyIfLatest(getActiveUsers, (state, payload) => {
        state.active = payload;
      }),
    );
    builder.addCase(
      getBlockedUsers.fulfilled,
      applyIfLatest(getBlockedUsers, (state, payload) => {
        state.blocked = payload;
      }),
    );
    builder.addCase(
      getFinanceStatistics.fulfilled,
      applyIfLatest(getFinanceStatistics, (state, payload) => {
        state.finances = payload;
      }),
    );

    // Matchers must come after every addCase.
    trackLatestRequests(builder, [
      getNewcomers,
      getActiveUsers,
      getBlockedUsers,
      getFinanceStatistics,
    ]);
  },
});

export const statisticsActions = slice.actions;

export const statisticsReducer = slice.reducer;
